import { Tabs } from '@scaleaq/scaleui';
import styled from 'styled-components';

import { colors, responsive } from '@/utils';

const tabRule = colors.stroke.neutral['50'];

/**
 * Shortcuts modal tab strip. Scale UI `Tabs` renders (see @scaleaq/scaleui `Tabs.js`):
 *
 * - Root: `Row` container — direct child of this styled component.
 * - `button` ×2 — `TabsSpacerStart` / `TabsSpacerEnd` (chevron / filler spacers beside the scroll area).
 * - `nav` — `ScrollRow`, holds the tab links and scrolls horizontally.
 * - `nav > a` — each `TabBase` (tab label / hit target).
 *
 * Default tab chrome uses action-colored borders; we align fill with the modal and use neutral stroke
 * for the active top / inactive bottom rules.
 */
export const ShortcutsTabs = styled(Tabs)`
  /* Root row: modal surface */
  background-color: ${colors.surface.default};

  /*
   * Spacer buttons (siblings of nav): same bottom edge as unselected tabs (continuous line under the row).
   */
  & > button {
    background-color: ${colors.surface.default};
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: ${tabRule};
  }

  /* Scroll container: no extra chrome, matches modal */
  & > nav {
    background-color: ${colors.surface.default};
  }

  /* All tabs: 1px rules (Scale UI defaults to 0.125rem). */
  & > nav > a {
    border-top-width: 1px;
    border-bottom-width: 1px;
  }

  /* Unselected: bottom accent only (top transparent). */
  & > nav > a:not([data-selected='true']) {
    background-color: ${colors.surface.default};
    border-top-color: transparent;
    border-bottom-color: ${tabRule};
  }

  & > nav > a:not([data-selected='true']):hover {
    background-color: ${colors.background.action.soft['100']};
  }

  /*
   * Selected tab: top accent. Doubled parent selector (&&) bumps specificity so this wins over TabBase
   * [data-selected="true"] border-top-color (action blue).
   */
  && > nav > a[data-selected='true']:not([data-disabled='true']) {
    border-top-color: ${tabRule};
    border-bottom-color: ${tabRule};
    border-top-left-radius: ${responsive.spacing.small};
    border-top-right-radius: ${responsive.spacing.small};
  }

  /* Dark mode: Scale UI's action-blue hover label has too little contrast on the dark blue hover fill. */
  :root[data-color-mode='dark'] & > nav > a:not([data-selected='true']):hover {
    color: ${colors.text.neutral.contrast};
  }
`;
